import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Assessment as AssessmentIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import MlEstimateBadge from '../../components/MlEstimateBadge';

function ConfidenceBadge({ confidence, n }) {
  if (confidence !== 'low') return null;
  return (
    <MlEstimateBadge
      basis="low_sample"
      label="Low sample"
      tooltip={
        n != null
          ? `Based on only ${n} publish outcomes — treat as directional until more data lands.`
          : 'Based on a small sample of publish outcomes — treat as directional.'
      }
    />
  );
}

/**
 * Phase 19.7: market signal from competitor scrapes, shown alongside first-party
 * learnings rather than merged into them. First-party engagement is thin (26 of 749
 * posts carry any), while the competitor corpus is far richer and grows nightly.
 * Competitor engagement averages orders of magnitude above client engagement, so the
 * two are never pooled -- and market numbers are relative multiples of each
 * competitor's own average, not raw counts, so a large account cannot dominate.
 */
function MarketSignalBlock({ title, market }) {
  if (!market) return null;
  const platforms = market.platforms || {};
  const platformKeys = Object.keys(platforms).filter(
    (k) => (platforms[k]?.posts_n || 0) > 0
  );
  const empty = (market.posts_n || 0) === 0 || platformKeys.length === 0;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">{title}</Typography>
          <Chip size="small" color="primary" label="Competitors" variant="outlined" />
          <Chip size="small" label={`${market.posts_n || 0} posts`} variant="outlined" />
          {market.competitors_n ? (
            <Chip
              size="small"
              label={`${market.competitors_n} competitor${market.competitors_n === 1 ? '' : 's'}`}
              variant="outlined"
            />
          ) : null}
          <ConfidenceBadge confidence={market.confidence} n={market.posts_n} />
        </Stack>

        {empty ? (
          <Typography variant="body2" color="text.secondary">
            No scraped competitor posts with timestamps yet. Market signal appears after
            competitor scrapes land — they run nightly.
          </Typography>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Relative engagement: 1.0 = that competitor&apos;s own average post. Normalised per
              account so a large competitor does not outweigh a small one.
            </Typography>
            <Grid container spacing={2}>
              {platformKeys.map((plat) => {
                const d = platforms[plat] || {};
                const slots = d.best_time_slots || [];
                const types = d.content_types || [];
                const cadence = d.cadence || {};
                return (
                  <Grid item xs={12} md={6} key={plat}>
                    <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                      {plat}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.posts_n} posts
                      {cadence.posts_per_week != null
                        ? ` · market cadence ${cadence.posts_per_week}/week`
                        : ''}
                    </Typography>

                    {slots.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Best posting slots
                        </Typography>
                        {slots.slice(0, 3).map((sl) => (
                          <Stack
                            key={`${plat}-${sl.day_of_week}-${sl.hour}`}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexWrap: 'wrap' }}
                          >
                            <Typography variant="body2">
                              {sl.label} — {sl.relative_engagement}×
                            </Typography>
                            <Chip size="small" label={`n=${sl.n}`} variant="outlined" />
                            <ConfidenceBadge confidence={sl.confidence} n={sl.n} />
                          </Stack>
                        ))}
                      </Box>
                    )}

                    {types.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Content types
                        </Typography>
                        {types.slice(0, 3).map((ct) => (
                          <Stack
                            key={`${plat}-${ct.content_type}`}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexWrap: 'wrap' }}
                          >
                            <Typography variant="body2">
                              {ct.content_type} — {ct.relative_engagement}×
                            </Typography>
                            <Chip size="small" label={`n=${ct.n}`} variant="outlined" />
                            <ConfidenceBadge confidence={ct.confidence} n={ct.n} />
                          </Stack>
                        ))}
                      </Box>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LearningsBlock({ title, block }) {
  if (!block) return null;
  const patterns = block.top_caption_patterns || [];
  const slots = block.best_time_slots || [];
  const shifts = block.content_type_shifts || [];
  const empty = (block.posts_n || 0) === 0;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">{title}</Typography>
          <Chip size="small" label={`${block.posts_n || 0} posts`} variant="outlined" />
          <ConfidenceBadge confidence={block.confidence} n={block.posts_n} />
        </Stack>
        {empty ? (
          <Typography variant="body2" color="text.secondary">
            No publish outcomes in this window yet. Learnings appear after posts have measured
            engagement.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Top caption patterns
              </Typography>
              {patterns.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {patterns.slice(0, 4).map((p) => (
                    <Box key={p.pattern}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {p.label}
                        </Typography>
                        <ConfidenceBadge confidence={p.confidence} n={p.n} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        avg eng {p.avg_engagement} · n={p.n}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Best engagement time slots
              </Typography>
              {slots.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Need posted hour/day on outcomes.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {slots.slice(0, 4).map((s) => (
                    <Box key={`${s.day_of_week}-${s.hour}`}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.label}
                        </Typography>
                        <ConfidenceBadge confidence={s.confidence} n={s.n} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        avg eng {s.avg_engagement} · n={s.n}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Content type performance shift
              </Typography>
              {shifts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {shifts.slice(0, 4).map((s) => (
                    <Box key={s.content_type}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.content_type}
                        </Typography>
                        <ConfidenceBadge confidence={s.confidence} n={s.n} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        this week {s.this_week_avg ?? '—'} vs prev {s.prev_week_avg ?? '—'}
                        {s.delta_pct != null
                          ? ` (${s.delta_pct >= 0 ? '+' : ''}${s.delta_pct}%)`
                          : ''}
                        {` · n=${s.n}`}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

const PERIOD_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

export default function MlModelMonitoringTab({
  driftAlerts,
  abEvaluation,
  abEvaluationLoading,
  weeklyLearnings,
  weeklyLearningsLoading,
  weeklyLearningsDays = 7,
  onWeeklyLearningsPeriodChange,
  captionEditLearning,
  captionEditLearningLoading,
  onRefresh,
  onTriggerBackfill,
}) {
  const pi = weeklyLearnings?.prediction_improvement;
  const clients = weeklyLearnings?.per_client || [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon /> Model Monitoring & A/B Evaluation
      </Typography>
      {driftAlerts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Drift Alerts ({driftAlerts.length})</Typography>
          {driftAlerts.map((a, i) => (
            <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>
              {a.message}
            </Typography>
          ))}
        </Alert>
      )}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => {
            onRefresh();
          }}
          disabled={abEvaluationLoading || weeklyLearningsLoading || captionEditLearningLoading}
        >
          Refresh
        </Button>
        <Button variant="outlined" size="small" onClick={onTriggerBackfill}>
          Trigger Backfill
        </Button>
      </Stack>

      {/* Phase 19.2: caption edit learning */}
      <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
        Caption edit learning
      </Typography>
      {captionEditLearningLoading && <CircularProgress size={24} sx={{ mb: 2 }} />}
      {!captionEditLearningLoading && !captionEditLearning && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Caption edit pair stats unavailable.
        </Alert>
      )}
      {!captionEditLearningLoading && captionEditLearning?.global && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip
                size="small"
                label={`${captionEditLearning.global.genuine_pair_count || 0} genuine pairs`}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${captionEditLearning.global.total_edit_events || 0} edit events`}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${captionEditLearning.global.clients_meeting_threshold || 0} clients ready`}
                color={
                  (captionEditLearning.global.clients_meeting_threshold || 0) > 0
                    ? 'success'
                    : 'default'
                }
                variant="outlined"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
              Gallery shows &quot;Based on your edits&quot; when a client has more than{' '}
              {captionEditLearning.global.min_pairs_required - 1} confirmed genuine edit pairs.
            </Typography>
            {(captionEditLearning.per_client || []).slice(0, 8).map((c) => (
              <Box key={c.client_id} sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {c.client_name || `Client #${c.client_id}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.genuine_pair_count} genuine / {c.total_edit_events} events
                  {c.meets_threshold ? ' · suggestions active' : ' · below threshold'}
                </Typography>
                {c.patterns?.preferred_hashtags?.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Adds hashtags: {c.patterns.preferred_hashtags.map((t) => `#${t}`).join(' ')}
                  </Typography>
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Phase 15.1 / 19.4 */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 1 }}>
        <Typography variant="h6">
          {weeklyLearningsDays === 7 ? "This week’s learnings" : `Learnings — last ${weeklyLearningsDays} days`}
        </Typography>
        <Select
          size="small"
          value={weeklyLearningsDays}
          onChange={(e) => onWeeklyLearningsPeriodChange && onWeeklyLearningsPeriodChange(e.target.value)}
          disabled={weeklyLearningsLoading}
          sx={{ minWidth: 110 }}
        >
          {PERIOD_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      </Stack>
      {weeklyLearningsLoading && <CircularProgress size={24} sx={{ mb: 2 }} />}
      {!weeklyLearningsLoading && !weeklyLearnings && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No weekly learnings yet. They appear after the Sunday train pipeline (or when publish
          outcomes exist and this page loads).
        </Alert>
      )}
      {!weeklyLearningsLoading && weeklyLearnings && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {weeklyLearningsDays === 7 ? 'Week' : `${weeklyLearningsDays}-day window`}{' '}
              {weeklyLearnings.week_start} → {weeklyLearnings.week_end}
            </Typography>
            {weeklyLearnings.model_version && (
              <Chip size="small" label={weeklyLearnings.model_version} variant="outlined" />
            )}
            <Chip size="small" label="Publish outcomes" color="success" variant="outlined" />
          </Stack>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                <Typography variant="subtitle1">Prediction improvement vs last week</Typography>
                <ConfidenceBadge confidence={pi?.confidence} n={pi?.n} />
              </Stack>
              {pi?.this_week_mae == null ? (
                <Typography variant="body2" color="text.secondary">
                  Not enough prediction-vs-actual pairs yet to compare weeks.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    MAE this week: {pi.this_week_mae}
                    {pi.last_week_mae != null ? ` · last week: ${pi.last_week_mae}` : ''}
                    {pi.delta_pct != null
                      ? ` (${pi.improved ? 'improved' : 'worsened'} ${Math.abs(pi.delta_pct)}%)`
                      : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    From logged predictions with measured actuals (n={pi.n}). Lower MAE is better.
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* 24.0a: fleet-wide blocks pool every tenant and arrive anonymized, Class B. */}
          <LearningsBlock
            title="All clients (fleet, anonymized · Class B)"
            block={weeklyLearnings.global}
          />
          {clients.slice(0, 6).map((c) => (
            <LearningsBlock
              key={c.client_id}
              title={c.client_name || `Client #${c.client_id}`}
              block={c}
            />
          ))}

          {/* Phase 19.7: what the market does, kept visually distinct from the
              first-party blocks above so the two are never read as one number. */}
          <MarketSignalBlock
            title="Market signal — all competitors (fleet · Class B)"
            market={weeklyLearnings.market}
          />
          {clients.slice(0, 6).map((c) =>
            c.market ? (
              <MarketSignalBlock
                key={`market-${c.client_id}`}
                title={`Market signal — ${c.client_name || `Client #${c.client_id}`}`}
                market={c.market}
              />
            ) : null
          )}
        </Box>
      )}

      {abEvaluationLoading && <CircularProgress size={24} sx={{ mt: 2 }} />}
      {!abEvaluationLoading && abEvaluation && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Calibration (Predicted vs Actual)
                </Typography>
                {abEvaluation.calibration?.overall?.n != null &&
                abEvaluation.calibration.overall.n > 0 ? (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      MAE: {(abEvaluation.calibration.overall.mae ?? 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      RMSE: {(abEvaluation.calibration.overall.rmse ?? 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      Bias: {(abEvaluation.calibration.overall.bias ?? 0).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      n = {abEvaluation.calibration.overall.n} posts
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No calibration data yet. Run backfill to populate.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Drift Detection
                </Typography>
                {abEvaluation.drift?.status && (
                  <Stack spacing={1}>
                    <Chip
                      label={abEvaluation.drift.status}
                      color={
                        abEvaluation.drift.status === 'stable'
                          ? 'success'
                          : abEvaluation.drift.status === 'moderate_drift'
                            ? 'warning'
                            : 'error'
                      }
                      size="small"
                    />
                    {abEvaluation.drift.max_psi != null && (
                      <Typography variant="body2">
                        Max PSI: {abEvaluation.drift.max_psi.toFixed(3)}
                        {abEvaluation.drift.max_psi_raw != null &&
                          abEvaluation.drift.max_psi_raw !== abEvaluation.drift.max_psi && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              (raw {abEvaluation.drift.max_psi_raw.toFixed(3)} incl. schema
                              features)
                            </Typography>
                          )}
                      </Typography>
                    )}
                    {(abEvaluation.drift.schema_annotations || []).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Schema-change annotations
                        </Typography>
                        {(abEvaluation.drift.schema_annotations || []).map((ann) => {
                          const label =
                            ann.display_label ||
                            (ann.baseline_reset_at
                              ? `${ann.label || ann.id} [${String(ann.baseline_reset_at).slice(0, 10)}]`
                              : ann.label || ann.id);
                          return (
                            <Alert
                              key={ann.id || label}
                              severity="info"
                              sx={{ mt: 0.75, py: 0.5 }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {label}
                              </Typography>
                              {ann.reason && (
                                <Typography variant="caption" color="text.secondary">
                                  {ann.reason}
                                </Typography>
                              )}
                            </Alert>
                          );
                        })}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Evaluated at: {abEvaluation.evaluated_at}
                    </Typography>
                  </Stack>
                )}
                {!abEvaluation.drift?.status && (
                  <Typography variant="body2" color="text.secondary">
                    No drift data yet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {!abEvaluationLoading && !abEvaluation && (
        <Alert severity="info">No model monitoring data available.</Alert>
      )}
    </Box>
  );
}
